import React from 'react';
import { AlertCircle, ChevronDown, MapPin } from 'lucide-react';

interface StateDropdownProps {
  value: string;
  onChange: (value: string) => void;
  states: Array<{ name: string; iso2: string }>;
  isLoading: boolean;
  error?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
}

export const StateDropdown: React.FC<StateDropdownProps> = ({
  value,
  onChange,
  states,
  isLoading,
  error,
  placeholder = "Select State",
  label = "State",
  required = false,
  className = ""
}) => {
  const getStateName = (iso2: string) => {
    const state = states.find(s => s.iso2 === iso2);
    return state?.name || iso2;
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {!required && <span className="text-gray-400 font-normal ml-1">(optional)</span>}
      </label>
      
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isLoading}
          className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 appearance-none pr-10 ${
            error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : value
              ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
              : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
          } ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <option value="">{placeholder}</option>
          {states.map((state) => (
            <option key={state.iso2} value={state.iso2}>
              {state.name}
            </option>
          ))}
        </select>
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-gray-300 border-t-brand-500 rounded-full animate-spin"></div>
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
        
        {value && (
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-500" />
        )}
      </div>
      
      {error && (
        <p className="flex items-center gap-1 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </p>
      )}
      
      {value && !error && (
        <p className="text-sm text-gray-600">
          Selected: {getStateName(value)}
        </p>
      )}
    </div>
  );
};
